import React from 'react';
import PropTypes from 'prop-types';

import errorIcon from '../../../assets/images/error-icon-container.svg';
import closeIcon from '../../../assets/images/input-close-icon.svg';

/**
 * CheckoutErrorModal Component
 *
 * Displays a modal dialog for checkout-related errors (payment failures, cache errors, etc.).
 * Allows users to close and try again. The modal can be used for various checkout error scenarios.
 *
 * @param {Function} onClose - Callback function called when modal is closed
 * @param {string} [title] - Optional custom title (defaults to "Unable to Complete Subscription Request")
 * @param {string} [messagePrimary] - Optional custom primary message (defaults to standard subscription error message)
 * @param {string} [messageSecondary] - Optional custom secondary message (defaults to standard subscription error details)
 */
const CheckoutErrorModal = ({
  onClose,
  title,
  messagePrimary,
  messageSecondary,
}) => {
  const defaultTitle = 'Unable to Complete Subscription Request';
  const defaultPrimaryMessage =
    "We're unable to complete your request to sign up for an Elevated Profile Subscription.";
  const defaultSecondaryMessage =
    'Please try again later or contact us if you need assistance.';

  const modalTitle = title || defaultTitle;
  const primaryMessage = messagePrimary || defaultPrimaryMessage;
  const secondaryMessage = messageSecondary || defaultSecondaryMessage;

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-[640px] flex flex-col items-center gap-5 bg-white pt-8 pb-10 rounded"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-error-modal-title"
        >
          {/* Close button */}
          <div className="flex justify-end gap-2.5 self-stretch px-6">
            <button
              type="button"
              onClick={onClose}
              className="w-4 h-4 flex items-center justify-center cursor-pointer transition-opacity duration-300 hover:opacity-80"
              aria-label="Close modal"
            >
              <img src={closeIcon} alt="Close icon" />
            </button>
          </div>

          {/* Error icon */}
          <div className="flex justify-center items-center">
            <img
              src={errorIcon}
              alt="Error icon"
              className="w-[60px] h-[60px]"
            />
          </div>

          {/* Error message */}
          <div className="flex flex-col gap-3 self-stretch px-[60px]">
            <span
              id="checkout-error-modal-title"
              className="font-semibold text-[20px] leading-[32px] text-center text-coral-90"
            >
              {modalTitle}
            </span>
            <div className="flex flex-col items-center self-stretch">
              <span className="text-base text-center font-semibold text-gray-90 px-12">
                {primaryMessage}
              </span>
              <span className="text-base text-center font-extralight mt-3 text-gray-90">
                {secondaryMessage}
              </span>
            </div>
          </div>

          {/* Close button */}
          <div className="flex flex-col items-center gap-4 self-stretch bg-white pt-6">
            <div className="w-[528px] flex justify-center items-center gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex justify-center items-center gap-2.5 px-8 py-3 rounded-[99px] border border-solid border-deep-blue-30 cursor-pointer transition-colors duration-300 hover:bg-deep-blue-05"
              >
                <span className="font-extrabold text-[14px] leading-[16px] text-center text-deep-blue-30">
                  Close
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CheckoutErrorModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  messagePrimary: PropTypes.string,
  messageSecondary: PropTypes.string,
};

export default CheckoutErrorModal;
